import {
  type MappedFormDataObject,
  type MutationVariables,
  FormDataObjectType,
} from '../types/webform'

/**
 * Map a form data value to a WebformSubmissionElement compatible value.
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export function mapFormDataValue(v: any): string | string[] {
  if (Array.isArray(v)) {
    return v.map(mapFormDataValue).flat()
  } else if (v === true) {
    return '1'
  } else if (v === false) {
    return '0'
  } else if (v === undefined || v === null) {
    return ''
  } else if (typeof v === 'string') {
    return v
  } else if (typeof v === 'number') {
    return v.toString()
  }
  return ''
}

function isUploadLike(value: unknown): boolean {
  if (value instanceof File) {
    return true
  }
  return (
    typeof value === 'object' &&
    value !== null &&
    'file' in value &&
    (value as { file: unknown }).file instanceof File
  )
}

/**
 * True when every item is a scalar (checkbox / multi-value groups).
 * Nested composites are objects; file uploads are File-like.
 */
export function isScalarValueArray(value: unknown[]): boolean {
  return value.every(
    (item) =>
      item === null ||
      item === undefined ||
      typeof item === 'string' ||
      typeof item === 'number' ||
      typeof item === 'boolean',
  )
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function mapFormDataObject(
  v: Record<string, any>,
): MappedFormDataObject[] {
  return Object.entries(v)
    .map(([element, valueRaw]) => {
      if (Array.isArray(valueRaw)) {
        // Checkbox groups and other multi-value scalars must stay one element
        // with an array value. graphql_webform / Drupal Checkboxes::valueCallback
        // only accepts arrays; a string is coerced to [] and fails #required.
        if (isScalarValueArray(valueRaw)) {
          return [
            {
              type: FormDataObjectType.ELEMENT,
              element,
              value: valueRaw.map(mapFormDataValue).flat(),
            },
          ]
        }

        return valueRaw
          .map((nestedItem, index) => {
            if (
              typeof nestedItem === 'object' &&
              nestedItem !== null &&
              !isUploadLike(nestedItem)
            ) {
              /* eslint-disable  @typescript-eslint/no-explicit-any */
              const newObj: Record<string, any> = {}
              Object.keys(nestedItem).forEach((key) => {
                const indexedKey = key.replace('[0]', `[${index}]`)
                newObj[indexedKey] = nestedItem[key]
              })
              return mapFormDataObject(newObj)
            }
            if (
              nestedItem &&
              typeof nestedItem === 'object' &&
              'file' in nestedItem &&
              nestedItem.file instanceof File
            ) {
              return {
                type: FormDataObjectType.FILE,
                element,
                value: nestedItem.file,
              }
            }
            const value = mapFormDataValue(nestedItem)
            return { element, value, type: FormDataObjectType.ELEMENT }
          })
          .flat()
      }
      if (
        typeof valueRaw === 'object' &&
        !Array.isArray(valueRaw) &&
        !(valueRaw instanceof File)
      ) {
        return mapFormDataObject(valueRaw)
      }
      if (valueRaw instanceof File) {
        return {
          type: FormDataObjectType.FILE,
          element,
          value: valueRaw,
        }
      }
      const value = mapFormDataValue(valueRaw)
      return { element, value, type: FormDataObjectType.ELEMENT }
    })
    .flat()
}

/**
 * Map the form data object to mutation variables for submitWebform.
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export function mapFormData(v: Record<string, any>): MutationVariables {
  const items = mapFormDataObject(v)

  return items.reduce<MutationVariables>(
    (acc, item) => {
      if (item.type === FormDataObjectType.ELEMENT) {
        acc.elements.push(item)
      } else {
        acc.files.push(item)
      }
      return acc
    },
    { elements: [], files: [] },
  )
}
